/**
 * Admin V3 — Financial / Trésorerie — parent navigation hub
 *
 * Mounted at: /admin-v3/financial (see AdminV3.js)
 *
 * GOVERNANCE — STATIC NAVIGATION HUB:
 *   - ZERO HTTP calls. ZERO data fetching. ZERO polling. ZERO WebSocket.
 *   - Pure presentation surface. No useAdminQuery. No useAdminMutation.
 *   - Three navigation cards linking to the three Trésorerie sub-modules
 *     (Ledger, Withdrawals, Payouts).
 *   - No RBAC fragility at this level: only the AdminRoute guard is enforced;
 *     no per-endpoint permission required for the hub itself.
 *
 * REPLACES the prior `<ModulePlaceholder>` for `/admin/financial` so
 * operators clicking "Trésorerie" in the sidebar land on a real navigation
 * page instead of "Module en préparation".
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Euro, BookOpen, Wallet, Coins, ChevronRight } from 'lucide-react';
import { SectionCard, cn } from '../../shared/visualTokens';
import { t, DEFAULT_LOCALE } from '../../i18n';

const HubCard = ({ icon: Icon, title, description, to, testId }) => (
  <Link
    to={to}
    className={cn(
      'group rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_4px_18px_rgba(15,23,42,0.04)]',
      'hover:border-indigo-200 hover:bg-slate-50/60 transition-colors block'
    )}
    data-testid={testId}
  >
    <div className="flex items-start gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-100 text-indigo-600 flex-shrink-0">
        <Icon className="h-5 w-5" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-[15px] font-semibold text-slate-900 group-hover:text-indigo-700">
            {title}
          </h3>
          <ChevronRight className="h-4 w-4 text-slate-300 group-hover:text-indigo-500 flex-shrink-0" />
        </div>
        <p className="mt-1 text-[12px] text-slate-500 leading-relaxed">
          {description}
        </p>
      </div>
    </div>
  </Link>
);

const FinancialHubPage = () => {
  const locale = DEFAULT_LOCALE;

  return (
    <SectionCard
      title={t('financial.hub.title', locale)}
      icon={Euro}
      iconClass="bg-indigo-100 text-indigo-600"
    >
      <div className="space-y-4">
        {/* Subtitle: read-only governance posture */}
        <p
          className="text-[11px] italic text-slate-400 -mt-2"
          data-testid="adminv3-financial-hub-subtitle"
        >
          {t('financial.hub.subtitle', locale)}
        </p>

        {/* 3 navigation cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="adminv3-financial-hub-cards">
          <HubCard
            icon={BookOpen}
            title={t('financial.hub.cards.ledger.title', locale)}
            description={t('financial.hub.cards.ledger.desc', locale)}
            to="/admin/financial/ledger"
            testId="adminv3-financial-hub-card-ledger"
          />
          <HubCard
            icon={Wallet}
            title={t('financial.hub.cards.withdrawals.title', locale)}
            description={t('financial.hub.cards.withdrawals.desc', locale)}
            to="/admin/financial/withdrawals"
            testId="adminv3-financial-hub-card-withdrawals"
          />
          <HubCard
            icon={Coins}
            title={t('financial.hub.cards.payouts.title', locale)}
            description={t('financial.hub.cards.payouts.desc', locale)}
            to="/admin/financial/payouts"
            testId="adminv3-financial-hub-card-payouts"
          />
        </div>
      </div>
    </SectionCard>
  );
};

export default FinancialHubPage;
